// frontend/nidoux/react-functions/src/NidouxApp.js
import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import LoginScreen from './components/LoginScreen';
import TermsAndConditions from './components/Terms';
import './i18n';
import i18n from 'i18next';
import { getApiBaseUrl, getAppIdentifier } from './config';
import api from './services/api';
import { useDeepLinks } from './hooks/useDeepLinks';
import { saveRememberToken, getRememberToken, clearRememberToken } from './services/authStorage';
import { usePushNotifications } from './hooks/usePushNotifications';
import { useVersionCheck } from './hooks/useVersionCheck';

// Lazy-load dashboards – each role only downloads its own bundle
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const TeacherDashboard = lazy(() => import('./components/TeacherDashboard'));
const ParentDashboard = lazy(() => import('./components/ParentDashboard'));

// Where a push tap or a link should land: { type: 'thread' | 'approval', id },
// or { type: 'absence' } for an absence push (the dashboard opens its absence
// screen). Every call makes a new object, so tapping the same item twice opens it twice.
// Ids are Mongo ObjectIds; anything else is ignored.
const OBJECT_ID = /^[a-f0-9]{24}$/i;
const makeTarget = (type, id) => (OBJECT_ID.test(String(id || '')) ? { type, id: String(id) } : null);

// E-mail links: /thread/<id> for a conversation, /message/<id> for an approval request.
function targetFromPath(pathname) {
  const m = /^\/(thread|message)\/([^/?#]+)/.exec(pathname || '');
  return m ? makeTarget(m[1] === 'thread' ? 'thread' : 'approval', m[2]) : null;
}

function NidouxApp() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  // Where to land: set by a push tap, an app link or the URL of an e-mail
  // link, and held here until a dashboard takes it, so a tap that wakes the
  // app before (auto-)login is not lost.
  const [navTarget, setNavTarget] = useState(() => targetFromPath(window.location.pathname));
  const [isAutoLogging, setIsAutoLogging] = useState(true);

  // ✅ NEW: Check if we're on the public terms page
  const isPublicTermsPage = window.location.pathname === '/terms';

  // Auto-login with stored remember token on app startup
  useEffect(() => {
    if (isPublicTermsPage) {
      setIsAutoLogging(false);
      return;
    }

    const attemptAutoLogin = async () => {
      const token = getRememberToken();
      if (!token) {
        setIsAutoLogging(false);
        return;
      }

      try {
        const appId = getAppIdentifier();
        const response = await fetch(`${getApiBaseUrl()}/login/token-auth`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-App-Identifier': appId || ''
          },
          credentials: 'include',
          body: JSON.stringify({ rememberToken: token })
        });

        if (response.ok) {
          const data = await response.json();
          // Save the rotated token
          if (data.rememberToken) {
            saveRememberToken(data.rememberToken);
          }
          setIsLoggedIn(true);
          setUserData({
            ...data,
            userType: data.isAdmin ? 'admin' : data.userType
          });
          // Sync language on auto-login (fire-and-forget)
          api.saveLanguage(i18n.language || 'fr').catch(() => {});
        } else if (response.status === 401 || response.status === 403) {
          // Token invalid or expired — clear it
          clearRememberToken();
        }
        // Anything else (a rate limit, a server error) says nothing about the
        // token: keep it, so the next start logs in again by itself instead of
        // asking the family for their access code.
      } catch (err) {
        // No network: same, keep the token.
        console.error('Auto-login failed:', err);
      } finally {
        setIsAutoLogging(false);
      }
    };

    attemptAutoLogin();
  }, [isPublicTermsPage]);

  const handleLoginSuccess = (data) => {
    // Save remember token for auto-login on next app start
    if (data.rememberToken) {
      saveRememberToken(data.rememberToken);
    }

    setIsLoggedIn(true);
    setUserData({
      ...data,
      userType: data.isAdmin ? 'admin' : data.userType
    });
    setShowTerms(false);

    // Sync current language to backend for localized push notifications (fire-and-forget)
    api.saveLanguage(i18n.language || 'fr').catch(() => {});
  };

  const handleLogout = async () => {
    // Clear stored token so auto-login won't happen
    clearRememberToken();

    // Unregister push token before logging out. Logging out is not opting out:
    // the next login on this device registers again.
    try { await unregisterTokenRef.current?.({ keepPreference: true }); } catch (_) {}

    try {
      const appId = getAppIdentifier();
      await fetch(`${getApiBaseUrl()}/logout`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-App-Identifier': appId || ''
        }
      });

      setIsLoggedIn(false);
      setUserData(null);
      setShowTerms(false);
      setNavTarget(null);
      sessionStorage.clear();
    } catch (error) {
      console.error('Logout error:', error);
      setIsLoggedIn(false);
      setUserData(null);
      setShowTerms(false);
      setNavTarget(null);
      sessionStorage.clear();
    }
  };

  const handleShowTerms = () => {
    setShowTerms(true);
  };

  const handleBackToLogin = () => {
    setShowTerms(false);
  };

  // ✅ NEW: For public terms page, go to home
  const handleBackToHome = () => {
    window.location.href = '/';
  };

  // Push notification tap — open what it is about. Payloads: { type: 'message',
  // threadId } and { type: 'approval', messageId } open the conversation or
  // approval request; { type: 'absence_request' } (to teachers) and
  // { type: 'absence_decision' } (to parents) open the absence screen.
  const handleNotificationTap = useCallback((data) => {
    let target = null;
    if (data?.type === 'message') {
      target = makeTarget('thread', data.threadId);
      // A quiz push (a reminder, the results): open the conversation on
      // that message, not at its newest one.
      if (target && data.quiz && OBJECT_ID.test(String(data.messageId || ''))) target.focusMessageId = String(data.messageId);
    } else if (data?.type === 'approval') target = makeTarget('approval', data.messageId);
    else if (data?.type === 'absence_request' || data?.type === 'absence_decision') target = { type: 'absence' };
    if (target) setNavTarget(target);
  }, []);

  // A dashboard took the target. Drop an e-mail link's path too, so a reload
  // does not open the same item again.
  const handleNavTargetConsumed = useCallback(() => {
    setNavTarget(null);
    if (targetFromPath(window.location.pathname)) {
      window.history.replaceState(null, '', '/');
    }
  }, []);

  // Push notifications — registers token when logged in
  const { registerPush, unregisterToken, isRegistered, checkOSPermission } = usePushNotifications({
    userId: isLoggedIn ? userData?.userId : null,
    onNotificationTap: handleNotificationTap,
  });

  // Store unregisterToken in a ref so handleLogout can access the latest version
  const unregisterTokenRef = useRef(unregisterToken);
  useEffect(() => { unregisterTokenRef.current = unregisterToken; }, [unregisterToken]);

  // Bundle push functions for dashboard bell toggle
  const pushNotifications = { registerPush, unregisterToken, isRegistered, checkOSPermission };

  // Version check — runs once on native app launch
  const { updateAvailable, forceUpdate, latestVersion, updateMessage, storeUrl, dismissed, dismiss } = useVersionCheck({
    enabled: isLoggedIn,
  });

  // App links (Universal Links / App Links): the same targets as e-mail URLs.
  // Stable callback, so the listener is not re-registered on every render.
  const handleDeepLink = useCallback((link) => {
    let target = null;
    if (link?.type === 'thread') target = makeTarget('thread', link.threadId);
    else if (link?.type === 'message') target = makeTarget('approval', link.messageId);
    if (target) setNavTarget(target);
  }, []);
  useDeepLinks(handleDeepLink);

  // Suspense fallback – reuses the same spinner shown during auto-login
  const dashboardFallback = (
    <div style={{
      height: '100vh',
      width: '100vw',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#ffffff'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid #e0e0e0',
        borderTopColor: '#4A90D9',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const renderDashboard = () => {
    const versionProps = { updateAvailable, forceUpdate, latestVersion, updateMessage, storeUrl, dismissed, dismiss };
    const navProps = { navTarget, onNavTargetConsumed: handleNavTargetConsumed };
    let dashboard;
    if (userData?.isAdmin) {
      dashboard = <AdminDashboard userData={userData} onLogout={handleLogout} pushNotifications={pushNotifications} versionCheck={versionProps} {...navProps} />;
    } else if (userData?.userType === 'teacher') {
      dashboard = <TeacherDashboard userData={userData} onLogout={handleLogout} pushNotifications={pushNotifications} versionCheck={versionProps} {...navProps} />;
    } else if (userData?.userType === 'parent') {
      dashboard = <ParentDashboard userData={userData} onLogout={handleLogout} pushNotifications={pushNotifications} versionCheck={versionProps} {...navProps} />;
    } else {
      dashboard = <AdminDashboard userData={userData} onLogout={handleLogout} pushNotifications={pushNotifications} versionCheck={versionProps} {...navProps} />;
    }
    return <Suspense fallback={dashboardFallback}>{dashboard}</Suspense>;
  };

  // ✅ NEW: If on public terms page, show terms without login requirement
  if (isPublicTermsPage) {
    return (
      <div style={{ height: '100vh', width: '100vw' }}>
        <TermsAndConditions onBack={handleBackToHome} />
      </div>
    );
  }

  // Show a blank screen while checking for stored token (prevents login screen flash)
  if (isAutoLogging) {
    return (
      <div style={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #e0e0e0',
          borderTopColor: '#4A90D9',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', width: '100vw' }}>
      {!isLoggedIn ? (
        showTerms ? (
          <TermsAndConditions onBack={handleBackToLogin} />
        ) : (
          <LoginScreen
            onLoginSuccess={handleLoginSuccess}
            onShowTerms={handleShowTerms}
          />
        )
      ) : (
        renderDashboard()
      )}
    </div>
  );
}

export default NidouxApp;